import type { GraphEdge, GraphNode, NodeDefBase, PortDef } from '../graph/nodeDefs.js';
import type { RuntimeStateSchema } from '../graph/eventGraph.js';
import type { DataSourceDef, QueryGraphDef } from '../graph/dataGraph.js';
import { dataFieldPathLabel } from '../graph/dataSourceSchema.js';
import { queryNodeSourceIds } from '../graph/queryFieldSchema.js';
import type { RecordContextFieldOption } from '../state/recordContextSchema.js';
import type { SvgPathShape } from '../state/appState.js';
import { getNodeDataRecord } from '../graph/nodeDefs.js';
import { createDragger } from './controls/dragField.js';
import {
  createNodeDragLabel,
  createNodeFieldRow,
  createNodePortLabel,
  createNodePortRow,
  createNodeValueLabel,
} from './nodeFieldEditorControls.js';

interface RenderDeps {
  scheduleRender: () => void;
  updateParams: () => void;
  setupPortDrag: (dot: HTMLElement) => void;
  renderGraph: () => void;
  setNodeData: (nodeId: string, field: string, value: unknown) => void;
  runtimeState?: RuntimeStateSchema;
  dataSources?: Record<string, DataSourceDef>;
  shapes?: Record<string, SvgPathShape>;
  queryGraphs?: Record<string, QueryGraphDef>;
  graphNodes?: GraphNode[];
  graphEdges?: GraphEdge[];
  recordContextFields?: Record<string, RecordContextFieldOption>;
  setRuntimeStateReference?: (node: GraphNode, field: string, stateName: string) => void;
  setLookupSource?: (node: GraphNode, sourceId: string) => void;
  setLookupField?: (node: GraphNode, field: string) => void;
  invokeNodeAction?: (node: GraphNode, action: 'convert-shape-to-path') => void;
}

export function renderUIRows(node: GraphNode, container: HTMLElement, def: NodeDefBase, deps: RenderDeps): void {
  const ui = def.ui!;
  const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
  const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;
  const data = getNodeDataRecord(node);

  // Build port lookup: name → { portDef, io, index }
  const portMap = new Map<string, { port: PortDef; io: 'in' | 'out'; index: number }>();
  const renderedPorts = new Set<string>();
  inputs.forEach((p, i) => {
    const entry = { port: p, io: 'in' as const, index: i };
    portMap.set(`in:${p.name}`, entry);
    if (!portMap.has(p.name)) portMap.set(p.name, entry);
  });
  outputs.forEach((p, i) => {
    const entry = { port: p, io: 'out' as const, index: i };
    portMap.set(`out:${p.name}`, entry);
    portMap.set(p.name, entry);
  });

  const renderPort = (entry: { port: PortDef; io: 'in' | 'out'; index: number }): void => {
    const { port, io, index } = entry;
    const key = `${io}:${port.name}`;
    if (renderedPorts.has(key)) return;
    renderedPorts.add(key);

    const el = createNodePortRow();
    const dot = document.createElement('div');
    dot.className = `port-dot ${io} type-${port.type}`;
    dot.dataset.node = node.id;
    dot.dataset.port = String(index);
    dot.dataset.io = io;
    const lbl = createNodePortLabel(port.label ?? port.name, io);
    if (io === 'out') {
      el.append(lbl, dot);
    } else {
      el.append(dot, lbl);
    }
    if (port.range) {
      const d = port.range;
      const dragger = createDragger(
        Number(data[port.name] ?? def.defaults?.[port.name] ?? 0), d.min, d.max, d.step,
        value => {
          deps.setNodeData(node.id, port.name, value);
          deps.scheduleRender();
          deps.updateParams();
        },
        undefined,
        (d.subtype || d.unit) ? { subtype: d.subtype, unit: d.unit } : undefined,
      );
      dragger.style.width = '64px';
      dragger.style.marginLeft = '8px';
      dragger.style.marginRight = '8px';
      el.appendChild(dragger);
    }
    deps.setupPortDrag(dot);
    container.appendChild(el);
  };

  for (const row of ui) {
    const isStateReference = row.kind === 'text'
      && ((node.type === 'State' && (row.field === 'name' || row.field === 'state'))
        || ((node.type === 'Set' || node.type === 'Toggle' || node.type === 'Add') && row.field === 'state'));
    if (node.type === 'State' && row.kind === 'select' && (row.field === 'scope' || row.field === 'valueType')) continue;
    switch (row.kind) {
      case 'port': {
        const entry = portMap.get(row.io ? `${row.io}:${row.ref}` : row.ref);
        if (!entry) continue;
        renderPort(entry);
        break;
      }

      case 'select': {
        const el = createNodeFieldRow();

        el.appendChild(createNodeValueLabel(row.label));

        const select = document.createElement('select');
        select.className = 'node-value-input';
        select.style.width = '100%';
        for (const opt of row.options) {
          const o = document.createElement('option');
          o.value = opt.value;
          o.textContent = opt.label;
          if (opt.value === String(data[row.field] ?? row.default)) o.selected = true;
          select.appendChild(o);
        }
        select.addEventListener('mousedown', e => e.stopPropagation());
        select.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, select.value);
          deps.renderGraph();
          deps.scheduleRender();
        });
        el.appendChild(select);
        container.appendChild(el);
        break;
      }

      case 'data-source': {
        const el = createNodeFieldRow();
        el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select');
        select.className = 'node-value-input';
        select.style.width = '100%';
        const current = String(data[row.field] ?? '');
        if (!current || !deps.dataSources?.[current]) {
          select.appendChild(new Option(current ? 'Missing source' : 'Choose source', ''));
        }
        for (const id of Object.keys(deps.dataSources ?? {}).sort()) select.appendChild(new Option(id, id));
        select.value = deps.dataSources?.[current] ? current : '';
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          if (!select.value) return;
          if (deps.setLookupSource) deps.setLookupSource(node, select.value);
          else deps.setNodeData(node.id, row.field, select.value);
          deps.renderGraph();
          deps.scheduleRender();
        });
        el.appendChild(select);
        container.appendChild(el);
        break;
      }

      case 'shape-asset': {
        const el = createNodeFieldRow();
        el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select');
        select.className = 'node-value-input';
        select.style.width = '100%';
        const current = String(data[row.field] ?? '');
        if (!deps.shapes?.[current]) select.appendChild(new Option(current ? 'Missing shape' : 'Choose shape', ''));
        for (const [id, shape] of Object.entries(deps.shapes ?? {}).sort((a, b) => a[1].name.localeCompare(b[1].name))) {
          select.appendChild(new Option(shape.name || id, id));
        }
        select.value = deps.shapes?.[current] ? current : '';
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          if (!select.value) return;
          deps.setNodeData(node.id, row.field, select.value);
          deps.renderGraph();
          deps.scheduleRender();
        });
        el.appendChild(select);
        container.appendChild(el);
        break;
      }

      case 'action': {
        const el = createNodeFieldRow();
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ui-btn ui-btn--control';
        button.textContent = row.label;
        button.addEventListener('mousedown', event => event.stopPropagation());
        button.addEventListener('click', event => {
          event.stopPropagation();
          deps.invokeNodeAction?.(node, row.action);
        });
        el.appendChild(button);
        container.appendChild(el);
        break;
      }

      case 'path-summary': {
        const contours = Array.isArray(data.contours) ? data.contours : [];
        const anchors = contours.reduce((count, contour) => {
          const values = (contour as { anchors?: unknown }).anchors;
          return count + (Array.isArray(values) ? Math.floor(values.length / 2) : 0);
        }, 0);
        const el = createNodeFieldRow();
        el.appendChild(createNodeValueLabel('Contours'));
        const summary = document.createElement('span');
        summary.className = 'node-path-summary';
        summary.textContent = `${contours.length} · ${anchors} anchors`;
        el.appendChild(summary);
        container.appendChild(el);
        break;
      }

      case 'data-field': {
        const el = createNodeFieldRow();
        el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select');
        select.className = 'node-value-input';
        select.style.width = '100%';
        const source = deps.dataSources?.[String(data[row.sourceField] ?? '')];
        const current = String(data[row.field] ?? '');
        if (!current || !source?.fields[current]) {
          select.appendChild(new Option(current ? 'Missing field' : 'Choose field', ''));
        }
        for (const [fieldId, field] of Object.entries(source?.fields ?? {}).sort((a, b) => dataFieldPathLabel(a[1].path).localeCompare(dataFieldPathLabel(b[1].path)))) {
          select.appendChild(new Option(dataFieldPathLabel(field.path), fieldId));
        }
        select.disabled = !source;
        select.value = source?.fields[current] ? current : '';
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          if (!select.value) return;
          if (deps.setLookupField) deps.setLookupField(node, select.value);
          else deps.setNodeData(node.id, row.field, select.value);
          deps.renderGraph();
          deps.scheduleRender();
        });
        el.appendChild(select);
        container.appendChild(el);
        break;
      }

      case 'query-graph': {
        const el = createNodeFieldRow();
        el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select'); select.className = 'node-value-input'; select.style.width = '100%';
        const current = String(data[row.field] ?? '');
        if (!deps.queryGraphs?.[current]) select.appendChild(new Option(current ? 'Missing query' : 'Choose query', ''));
        for (const id of Object.keys(deps.queryGraphs ?? {}).sort()) select.appendChild(new Option(id, id));
        select.value = deps.queryGraphs?.[current] ? current : '';
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, select.value);
          deps.setNodeData(node.id, 'output', '');
          deps.renderGraph(); deps.scheduleRender();
        });
        el.appendChild(select); container.appendChild(el); break;
      }

      case 'query-output': {
        const el = createNodeFieldRow(); el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select'); select.className = 'node-value-input'; select.style.width = '100%';
        const graph = deps.queryGraphs?.[String(data[row.queryField] ?? '')];
        const supportedTypes = new Set(['float', 'bool', 'string', 'vec2', 'vec3', 'vec4']);
        const outputs = (graph?.nodes ?? [])
          .filter(candidate => candidate.type === 'Output' && supportedTypes.has(String(candidate.data.inputType ?? 'float')))
          .map(candidate => String(candidate.data.inputName ?? '')).filter(Boolean).sort();
        const current = String(data[row.field] ?? '');
        if (!outputs.includes(current)) select.appendChild(new Option(current ? 'Missing output' : 'Choose output', ''));
        outputs.forEach(output => select.appendChild(new Option(output, output)));
        select.value = outputs.includes(current) ? current : '';
        select.disabled = !graph;
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, select.value);
          const outputNode = graph?.nodes.find(candidate => candidate.type === 'Output' && candidate.data.inputName === select.value);
          const outputType = String(outputNode?.data.inputType ?? 'float');
          const valueType = outputType === 'float' ? 'number' : outputType;
          if (['number', 'bool', 'string', 'vec2', 'vec3', 'vec4'].includes(valueType)) deps.setNodeData(node.id, 'valueType', valueType);
          deps.renderGraph(); deps.scheduleRender();
        });
        el.appendChild(select); container.appendChild(el); break;
      }

      case 'query-field': {
        const el = createNodeFieldRow(); el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select'); select.className = 'node-value-input'; select.style.width = '100%';
        const current = String(data[row.field] ?? '');
        const fields = new Map<string, string>();
        const fieldTypes = new Map<string, string>();
        const sourceIds = queryNodeSourceIds(deps.graphNodes ?? [], deps.graphEdges ?? [], node.id);
        for (const sourceId of sourceIds) {
          const source = deps.dataSources?.[sourceId];
          if (!source) continue;
          for (const [fieldId, field] of Object.entries(source.fields)) {
            const label = dataFieldPathLabel(field.path);
            fields.set(fieldId, fields.has(fieldId) ? fieldId : `${label} · ${sourceId}`);
            fieldTypes.set(fieldId, field.type);
          }
        }
        if (!sourceIds.length) {
          select.appendChild(new Option('Connect records first', ''));
        } else if (!fields.has(current)) {
          select.appendChild(new Option(current ? 'Missing field' : 'Choose field', ''));
        }
        for (const [fieldId, label] of [...fields].sort((a, b) => a[1].localeCompare(b[1]))) select.appendChild(new Option(label, fieldId));
        select.value = fields.has(current) ? current : '';
        select.disabled = !sourceIds.length;
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          if (select.value) {
            deps.setNodeData(node.id, row.field, select.value);
            const type = fieldTypes.get(select.value);
            if (type) deps.setNodeData(node.id, 'valueType', type);
          }
          deps.renderGraph(); deps.scheduleRender();
        });
        el.appendChild(select); container.appendChild(el); break;
      }

      case 'record-field': {
        const el = createNodeFieldRow(); el.appendChild(createNodeValueLabel(row.label));
        const select = document.createElement('select'); select.className = 'node-value-input'; select.style.width = '100%';
        const current = String(data[row.field] ?? '');
        const fields = deps.recordContextFields ?? {};
        const entries = Object.entries(fields).sort((a, b) => a[1].label.localeCompare(b[1].label));
        if (!entries.length) select.appendChild(new Option('Assign data context first', ''));
        else if (!fields[current]) select.appendChild(new Option(current ? 'Missing field' : 'Choose field', ''));
        for (const [fieldId, field] of entries) select.appendChild(new Option(field.label, fieldId));
        select.value = fields[current] ? current : '';
        select.disabled = !entries.length;
        select.addEventListener('mousedown', event => event.stopPropagation());
        select.addEventListener('change', () => {
          const field = fields[select.value];
          if (!field) return;
          deps.setNodeData(node.id, row.field, select.value);
          deps.setNodeData(node.id, 'valueType', field.type);
          deps.renderGraph(); deps.scheduleRender();
        });
        el.appendChild(select); container.appendChild(el); break;
      }

      case 'checkbox': {
        const el = createNodeFieldRow('value-row--toggle');

        const label = document.createElement('label');
        label.className = 'value-toggle';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = Boolean(data[row.field] ?? row.default);
        input.addEventListener('mousedown', e => e.stopPropagation());
        input.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, input.checked);
          deps.renderGraph();
          deps.scheduleRender();
        });

        const text = document.createElement('span');
        text.textContent = row.label;

        label.appendChild(input);
        label.appendChild(text);
        el.appendChild(label);
        container.appendChild(el);
        break;
      }

      case 'section': {
        const el = createNodeFieldRow('value-row--section');
        el.appendChild(createNodeDragLabel('value-section-label', row.label));
        container.appendChild(el);
        break;
      }

      case 'text': {
        const el = createNodeFieldRow();
        if (row.label) el.appendChild(createNodeValueLabel(row.label));
        if (isStateReference && deps.runtimeState && deps.setRuntimeStateReference) {
          const select = document.createElement('select');
          select.className = 'node-value-input';
          select.style.width = '100%';
          const current = String(data[row.field] ?? '');
          const empty = new Option(current && !deps.runtimeState[current] ? 'Missing state' : 'Choose state', '');
          select.appendChild(empty);
          for (const name of Object.keys(deps.runtimeState).sort()) {
            select.appendChild(new Option(name, name));
          }
          select.value = deps.runtimeState[current] ? current : '';
          select.addEventListener('mousedown', event => event.stopPropagation());
          select.addEventListener('change', () => {
            if (!select.value) return;
            deps.setRuntimeStateReference!(node, row.field, select.value);
            deps.renderGraph();
            deps.scheduleRender();
          });
          el.appendChild(select);
          container.appendChild(el);
          break;
        }
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'node-value-input';
        if (row.placeholder) input.placeholder = row.placeholder;
        // Canonical graph serialization elides values equal to the node
        // default. The editor must still show that effective value — notably
        // numeric zero — rather than making it look unset.
        input.value = String(data[row.field] ?? def.defaults?.[row.field] ?? '');
        input.style.width = '100%';
        input.style.flex = '1';
        const readValue = (): unknown => {
          const valueType = row.valueTypeField ? data[row.valueTypeField] : 'string';
          if (valueType === 'number') return Number(input.value);
          if (valueType === 'bool') return input.value === 'true';
          if (valueType === 'vec2' || valueType === 'vec3' || valueType === 'vec4') {
            try { return JSON.parse(input.value); } catch { return input.value; }
          }
          return input.value;
        };
        input.addEventListener('input', () => {
          deps.setNodeData(node.id, row.field, readValue());
          deps.scheduleRender();
        });
        input.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, readValue());
          deps.scheduleRender();
        });
        el.appendChild(input);
        container.appendChild(el);
        break;
      }

      case 'textarea': {
        const el = createNodeFieldRow();
        const textarea = document.createElement('textarea');
        textarea.className = 'node-value-input';
        if (row.placeholder) textarea.placeholder = row.placeholder;
        textarea.value = (data[row.field] as string) || '';
        textarea.style.flex = '1';
        textarea.style.resize = 'none';
        textarea.rows = row.rows ?? 3;
        textarea.addEventListener('input', () => {
          deps.setNodeData(node.id, row.field, textarea.value);
          deps.scheduleRender();
        });
        textarea.addEventListener('change', () => {
          deps.setNodeData(node.id, row.field, textarea.value);
          deps.scheduleRender();
        });
        textarea.addEventListener('mousedown', e => e.stopPropagation());
        textarea.addEventListener('wheel', e => e.stopPropagation());
        el.appendChild(textarea);
        container.appendChild(el);
        break;
      }

      case 'dragger': {
        const el = createNodeFieldRow();

        el.appendChild(createNodeValueLabel(row.label));

        const visual: import('../ui/controls/dragField.js').DragFieldVisualOptions | undefined =
          (row.subtype || row.unit) ? { subtype: row.subtype, unit: row.unit } : undefined;

        const dragger = createDragger(
          Number(data[row.field] ?? 0),
          row.min, row.max, row.step,
          v => {
            deps.setNodeData(node.id, row.field, v);
            deps.scheduleRender();
          },
          undefined,
          visual,
        );
        dragger.style.flex = '1';
        el.appendChild(dragger);
        container.appendChild(el);
        break;
      }
    }
  }

  // UI schemas only choose where a port appears among controls.  A missing
  // declaration must never make a graph edge invisible or non-interactive.
  // Appending omitted ports keeps legacy/custom node definitions usable while
  // preserving the visual source of truth for all edge geometry.
  inputs.forEach((port, index) => renderPort({ port, io: 'in', index }));
  outputs.forEach((port, index) => renderPort({ port, io: 'out', index }));
}
